import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Paywall from '../components/Paywall';

const API = process.env.REACT_APP_BACKEND_URL;

const viviDrop = [
  { text: 'Hydration is the foundation of intimate wellness — drink 2L water today.', icon: 'fa-droplet' },
  { text: 'A 5-minute pelvic floor check-in this morning sets the tone for your day.', icon: 'fa-heart' },
  { text: 'Stress is the #1 suppressor of libido — take 3 deep breaths right now.', icon: 'fa-wind' },
  { text: 'Your body is doing extraordinary things. Notice it. Appreciate it today.', icon: 'fa-spa' },
  { text: 'Intimate wellness is not a luxury — it\'s a fundamental dimension of health.', icon: 'fa-star' },
];

export default function Home() {
  const { user, authHeader, isPremium } = useAuth();
  const navigate = useNavigate();
  const [assessmentResults, setAssessmentResults] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [paywall, setPaywall] = useState(false);
  const [journal, setJournal] = useState([]);
  const drop = viviDrop[new Date().getDay() % viviDrop.length];

  useEffect(() => {
    const done = localStorage.getItem('intiviv_onboarding_done');
    if (!done || done === '0') { navigate('/onboarding'); return; }

    axios.get(`${API}/api/assessments/my-results`, { headers: authHeader() }).then(r => setAssessmentResults(r.data.results || [])).catch(() => {});
    axios.get(`${API}/api/consultation/my-bookings`, { headers: authHeader() }).then(r => setBookings(r.data.bookings || [])).catch(() => {});
    axios.get(`${API}/api/journal?kind=vivi`, { headers: authHeader() }).then(r => setJournal(r.data.entries || [])).catch(() => {});

    // Day-3 paywall removed — app is free for now.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pick today's "Saved Wisdom" deterministically (same entry all day per user)
  const wisdomOfTheDay = (() => {
    if (!journal.length) return null;
    const d = new Date();
    const seed = d.getFullYear() * 1000 + d.getMonth() * 50 + d.getDate();
    return journal[seed % journal.length];
  })();
  const cleanWisdom = (s) => (s || '')
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .slice(0, 220);

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const firstName = user?.name?.split(' ')[0] || 'there';

  const quickActions = [
    { icon: 'fa-comment-dots',    label: 'Ask VIVI',              sub: 'Private wellness chat',      path: '/chat',         hue: '#D4AF37', tag: 'AI · 24/7' },
    { icon: 'fa-fire',            label: 'Intimacy Journeys',     sub: '6 guided programs',           path: '/journeys',     hue: '#E8B4B8', tag: '6 PROGRAMS' },
    { icon: 'fa-people-arrows',   label: 'Couple Exercises',      sub: 'Clinical rituals · 5 min',   path: '/exercises',    hue: '#F5C6B0', tag: 'FOR TWO' },
    { icon: 'fa-quote-left',      label: 'Affirmations',          sub: '36 daily affirmations',       path: '/affirmations', hue: '#FFE9A6', tag: 'DAILY' },
    { icon: 'fa-heart',           label: 'Private Space for Two', sub: "Couples' secure vault",       path: '/couple',       hue: '#E8B4B8', tag: 'PRIVATE' },
    { icon: 'fa-clipboard-check', label: 'Assessments',           sub: '16 wellness scores',          path: '/assessments',  hue: '#67C9C0', tag: '16 SCORES' },
    { icon: 'fa-calendar-check',  label: 'Book Consultation',     sub: 'with Dr. Vikram Dukle',       path: '/consult',      hue: '#25D366', tag: 'IN 2 HRS' },
    { icon: 'fa-book-open',       label: 'Learn',                 sub: '456 articles · Library',      path: '/learn',        hue: '#D4AF37', tag: '456 READS' },
    { icon: 'fa-bag-shopping',    label: 'INTIVIV™ Store',        sub: 'Curated Products',            external: 'https://www.intiviv.com', hue: '#FFE9A6', tag: 'SHOP' },
    { icon: 'fa-user-doctor',     label: 'Founder',               sub: 'Meet Dr. Vikram',             path: '/founder',      hue: '#4FB3A9', tag: 'STORY' },
  ];

  const isAdmin = user?.role === 'admin';

  const track = (event, label) => {
    axios.post(`${API}/api/track`, { event, label }, { headers: authHeader() }).catch(() => {});
  };

  const onAction = (a) => {
    track('quick_action', a.label);
    if (a.external) {
      window.open(a.external, '_blank', 'noopener');
    } else {
      navigate(a.path);
    }
  };

  return (
    <div className="home-shell" style={{ minHeight: '100vh', paddingBottom: 'calc(var(--bottom-h) + 28px)', position: 'relative' }}>
      {/* Front-page silk hero — logo overlays directly on the gold-silk backdrop */}
      <div
        className="home-silk-hero"
        aria-hidden="true"
        style={{ backgroundImage: "url('/assets/luxe-gold-silk-v2.png')" }}
      />
      {/* Gold particles falling — luxe ambient layer (purely decorative) */}
      <div className="gold-particles" aria-hidden="true">
        {Array.from({ length: 14 }).map((_, i) => (
          <span key={i} style={{
            '--left': `${(i * 7.3 % 100).toFixed(1)}%`,
            '--delay': `${(i * 1.1).toFixed(1)}s`,
            '--dur': `${(8 + (i % 5) * 1.7).toFixed(1)}s`,
            '--size': `${1 + (i % 3) * 0.6}px`,
            '--drift': `${(i % 2 === 0 ? 1 : -1) * (8 + (i % 4) * 6)}px`,
          }} />
        ))}
      </div>
      {/* INTIVIV brand mark — sits right beneath the gold silk wave */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', paddingTop: 90, paddingBottom: 8, position: 'relative' }}>
        <div className="brand-constellation" aria-hidden="true">
          <span style={{ '--x': '8%',  '--y': '18%', '--d': '0s'    }} />
          <span style={{ '--x': '22%', '--y': '76%', '--d': '0.6s'  }} />
          <span style={{ '--x': '38%', '--y': '8%',  '--d': '1.4s'  }} />
          <span style={{ '--x': '54%', '--y': '90%', '--d': '2.2s'  }} />
          <span style={{ '--x': '70%', '--y': '14%', '--d': '0.9s'  }} />
          <span style={{ '--x': '84%', '--y': '70%', '--d': '1.8s'  }} />
          <span style={{ '--x': '92%', '--y': '32%', '--d': '2.6s'  }} />
          <span style={{ '--x': '12%', '--y': '52%', '--d': '3.0s'  }} />
        </div>
        <div className="brand-mark-wrap" data-testid="home-brand-mark">
          <img src="/intiviv-logo.png?v=4" alt="INTIVIV" className="brand-mark-svg" />
        </div>
        <div data-testid="home-brand-tagline" style={{
          marginTop: 18,
          fontFamily: "'Cormorant Garamond', 'Playfair Display', Georgia, serif",
          fontStyle: 'italic',
          fontSize: '1.35rem',
          lineHeight: 1.3,
          textAlign: 'center',
          padding: '0 20px',
          background: 'linear-gradient(90deg, var(--gold) 0%, #FFE9A6 25%, var(--gold) 50%, #FFE9A6 75%, var(--gold) 100%)',
          backgroundSize: '200% 100%',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          animation: 'shimmerSlide 6s linear infinite',
          position: 'relative',
          zIndex: 2,
        }}>
          India's first Gynaecologist-Led AI<br />Intimate Wellness Ecosystem
        </div>
      </div>

      {/* INTIVIV Brand Pillars — Trust · Guidance · Personalisation */}
      <div className="brand-pillars top-pillars" data-testid="home-top-pillars">
        {[
          { img: '/pillar-trust.svg',     alt: 'Trust',          h: 'Trust',          sub: 'Confidential care without judgement' },
          { img: '/pillar-guidance.svg',  alt: 'Guidance',       h: 'Guidance',       sub: 'Medical-first, not guesswork' },
          { img: '/pillar-personal.svg',  alt: 'Personalisation',h: 'Personalisation',sub: 'Care unique, tailored & evolving' },
        ].map((p, idx) => (
          <motion.div
            key={p.h}
            className="brand-pillar"
            initial={{ opacity: 0, y: 22 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, delay: 0.18 + idx * 0.18, ease: [0.22, 0.9, 0.32, 1] }}
            whileHover={{ y: -3 }}
            data-testid={`home-pillar-${p.h.toLowerCase()}`}
          >
            <span className="brand-pillar-icon brand-pillar-img">
              <img src={p.img} alt={p.alt} />
            </span>
            <div className="brand-pillar-body">
              <div className="brand-pillar-h">{p.h}</div>
              <div className="brand-pillar-sub">{p.sub}</div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Header */}
      <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
        style={{ paddingTop: 8, paddingBottom: 8 }}>
        <div className="flex-between" style={{ marginBottom: 4 }}>
          <div>
            <div className="greeting-eyebrow">
              {greeting()}
              <span className="holo-ribbon" aria-hidden="true" />
            </div>
            <div className="home-greeting">{firstName}, <span className="gold-italic">welcome.</span></div>
          </div>
          <div
            style={{ width: 44, height: 44, borderRadius: '50%', background: 'transparent', border: '1px solid var(--gold)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', boxShadow: '0 0 14px rgba(212,175,55,0.25)', overflow: 'hidden' }}
            onClick={() => navigate('/you')}
            data-testid="home-profile-badge"
            title="Your profile"
          >
            <img src="/intiviv-logo.png?v=4" alt="INTIVIV" style={{ width: '100%', height: '100%', objectFit: 'contain', padding: 4 }} />
          </div>
        </div>
        {isAdmin && (
          <div
            style={{ marginTop: 12, padding: '10px 12px', background: 'rgba(212,175,55,0.10)', border: '1px solid var(--gold)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', fontSize: '0.79rem', color: 'var(--text)', fontFamily: 'var(--font-ui)' }}
            onClick={() => navigate('/admin')}
            data-testid="admin-banner"
          >
            <span><i className="fa-solid fa-shield-halved" style={{ color: 'var(--gold)', marginRight: 8 }} />Admin · Currently Free Mode · View Dashboard</span>
            <i className="fa-solid fa-chevron-right" style={{ color: 'var(--gold)', fontSize: '0.71rem' }} />
          </div>
        )}
        {!isPremium && false && (
          <div style={{ marginTop: 12, padding: '8px 12px', background: 'rgba(212,175,55,0.06)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', fontSize: '0.81rem', color: 'var(--text-muted)' }}
            onClick={() => setPaywall(true)} data-testid="home-free-banner">
            <span><i className="fa-solid fa-sparkles" style={{ color: 'var(--gold)', marginRight: 6 }} />INTIVIV is currently free — subscription added later</span>
            <i className="fa-solid fa-chevron-right" style={{ color: 'var(--gold)', fontSize: '0.71rem' }} />
          </div>
        )}
      </motion.div>

      {/* Daily VIVI Drop */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.1 }}>
        <div className="vivi-drop-card" onClick={() => navigate('/chat')} style={{ cursor: 'pointer' }}>
          <div className="drop-pill">Daily VIVI Insight</div>
          <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
            <svg width="36" height="36" viewBox="0 0 64 64" style={{ flexShrink: 0, marginTop: 2, filter: 'drop-shadow(0 0 8px rgba(212,175,55,0.5))' }} aria-hidden="true">
              <defs>
                <radialGradient id="petalGrad" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#FFE9A6" />
                  <stop offset="60%" stopColor="#D4AF37" />
                  <stop offset="100%" stopColor="#8C6E1C" />
                </radialGradient>
                <radialGradient id="coreGrad" cx="50%" cy="50%" r="50%">
                  <stop offset="0%" stopColor="#FFF6D6" />
                  <stop offset="100%" stopColor="#D4AF37" />
                </radialGradient>
              </defs>
              {/* 5 petals, rotated around center */}
              <g transform="translate(32 32)">
                {[0, 72, 144, 216, 288].map(angle => (
                  <ellipse key={angle} cx="0" cy="-14" rx="6.5" ry="13" fill="url(#petalGrad)" opacity="0.92" transform={`rotate(${angle})`} />
                ))}
                <circle r="6" fill="url(#coreGrad)" />
                <circle r="2.2" fill="#8C6E1C" />
                {/* tiny stem */}
                <path d="M0 14 Q3 22 1 30" stroke="#5A4A1A" strokeWidth="1.2" fill="none" strokeLinecap="round" />
              </g>
            </svg>
            <div>
              <p style={{ lineHeight: 1.7, color: 'var(--text)', marginBottom: 8, fontStyle: 'italic', fontFamily: 'var(--font-display)', fontSize: '1.1rem' }}>"{drop.text}"</p>
              <span className="vivi-cta-shimmer">TAP TO ASK VIVI →</span>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Your Saved Wisdom of the Day — surfaces a random Journal entry, deterministic per day */}
      {wisdomOfTheDay && (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.15 }}>
          <div
            className="vivi-drop-card"
            onClick={() => navigate('/you')}
            data-testid="home-wisdom-of-the-day"
            style={{ cursor: 'pointer', borderColor: 'rgba(212,175,55,0.32)' }}
          >
            <div className="drop-pill" style={{ background: 'linear-gradient(90deg, rgba(212,175,55,0.18), rgba(212,175,55,0.06))' }}>
              <i className="fa-solid fa-bookmark" style={{ marginRight: 6 }} />Your Saved Wisdom
            </div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <i className="fa-solid fa-quote-left" style={{ color: 'var(--gold)', fontSize: '0.98rem', marginTop: 4, flexShrink: 0, opacity: 0.6 }} />
              <div style={{ flex: 1 }}>
                <p style={{
                  fontFamily: 'var(--font-display)',
                  fontStyle: 'italic',
                  fontSize: '1.1rem',
                  lineHeight: 1.7,
                  color: 'var(--text)',
                  marginBottom: 8,
                }}>
                  {cleanWisdom(wisdomOfTheDay.content)}{(wisdomOfTheDay.content || '').length > 220 ? '…' : ''}
                </p>
                <span className="vivi-cta-shimmer">VIEW IN JOURNAL →</span>
              </div>
            </div>
          </div>
        </motion.div>
      )}

      {/* Quick Actions */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.2 }}>
        <div className="quick-actions-eyebrow-wrap" data-testid="home-quick-actions-eyebrow">
          <span className="eyebrow">Your Wellness · One Tap Away</span>
          <span className="quick-actions-rule" aria-hidden="true" />
        </div>
        <motion.div
          className="quick-action-row premium-grid"
          variants={{ hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.05, delayChildren: 0.1 } } }}
          initial="hidden"
          animate="show"
        >
          {quickActions.map((a, i) => (
            <motion.button
              key={a.label}
              className="quick-action-v2"
              onClick={() => onAction(a)}
              variants={{ hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0, transition: { duration: 0.42, ease: [0.22, 0.9, 0.32, 1] } } }}
              whileHover={{ y: -3 }}
              whileTap={{ scale: 0.97 }}
              data-testid={`quick-action-${i}`}
              style={{ '--accent': a.hue }}
            >
              <span className="qa-rail" aria-hidden="true" />
              <span className="qa-icon-wrap" aria-hidden="true">
                <i className={`fa-solid ${a.icon}`} />
              </span>
              <span className="qa-body">
                <span className="qa-label">{a.label}</span>
                <span className="qa-sub">{a.sub}</span>
                {a.tag && <span className="qa-tag">{a.tag}</span>}
              </span>
              <span className="qa-chev" aria-hidden="true">
                <i className="fa-solid fa-arrow-right" />
              </span>
            </motion.button>
          ))}
        </motion.div>

        {/* Hero stats badge */}
        <div className="immersive-stats" data-testid="home-stats-badge">
          <div className="immersive-stat">
            <div className="immersive-stat-num">16</div>
            <div className="immersive-stat-lbl">Assessments</div>
          </div>
          <div className="immersive-divider" />
          <div className="immersive-stat">
            <div className="immersive-stat-num">456</div>
            <div className="immersive-stat-lbl">Articles</div>
          </div>
          <div className="immersive-divider" />
          <div className="immersive-stat">
            <div className="immersive-stat-num">14</div>
            <div className="immersive-stat-lbl">Languages</div>
          </div>
        </div>
      </motion.div>

      {/* CTA if no assessments */}
      {assessmentResults.length === 0 && (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
          style={{ margin: '8px 0', padding: '20px', background: 'var(--bg2)', border: '1px solid var(--border-soft)', textAlign: 'center', cursor: 'pointer' }}
          onClick={() => navigate('/assessments')}>
          <i className="fa-solid fa-clipboard-check" style={{ color: 'var(--gold)', fontSize: '1.8rem', marginBottom: 10, display: 'block' }} />
          <div style={{ fontFamily: 'var(--font-display)', fontSize: '1.2rem', marginBottom: 6 }}>Start Your Free Assessment</div>
          <div style={{ fontSize: '0.84rem', color: 'var(--text-muted)', marginBottom: 14 }}>Know your intimate wellness baseline in 3 minutes</div>
          <button className="btn-gold" style={{ fontSize: '0.79rem', padding: '10px 24px' }}>TAKE ASSESSMENT</button>
        </motion.div>
      )}

      {/* Upcoming booking */}
      {bookings.filter(b => b.status === 'confirmed').slice(0, 1).map(b => (
        <div key={b.booking_id} style={{ margin: '8px 0', padding: '16px', background: 'rgba(212,175,55,0.05)', border: '1px solid var(--border)' }}>
          <div className="eyebrow" style={{ marginBottom: 6 }}>Upcoming Consultation</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <i className="fa-solid fa-calendar-check" style={{ color: 'var(--gold)' }} />
            <div>
              <div style={{ fontSize: '0.93rem', color: 'var(--text)' }}>Dr. Vikram Dukle</div>
              <div style={{ fontSize: '0.79rem', color: 'var(--text-muted)' }}>{b.slot_date} at {b.slot_time?.slice(0,5)}</div>
            </div>
          </div>
        </div>
      ))}

      {paywall && (
        <Paywall trigger="You've explored INTIVIV for 3 days — ready to go deeper?" onClose={() => {
          setPaywall(false);
          localStorage.setItem('paywall_dismissed', '1');
        }} />
      )}

      {/* INTIVIV brand-story strip */}
      <div className="brand-story" data-testid="home-brand-story">
        <div className="brand-story-eyebrow">
          <span className="brand-story-eyebrow-text">India's First<br />Gynaecologist-Led</span>
        </div>
        <div className="brand-story-title">
          Intimate wellness has always <em>deserved</em> medical guidance.
        </div>
        <p className="brand-story-body">
          INTIVIV<sup style={{ fontSize: '0.6em', verticalAlign: 'super', marginLeft: 1, fontWeight: 500, letterSpacing: 0 }}>TM</sup> was born from a realisation: the world outside desperately needed
          medical guidance on intimate wellness — delivered with trust, without judgement,
          and without fear.
        </p>
        <p className="brand-story-body">
          Founded by Dr. Vikram Dukle, backed by 15+ years of listening
          to thousands of patients, speaking in half-sentences and long silences about
          their most private concerns.
        </p>
        <div className="brand-story-highlight" data-testid="brand-formulated-highlight">
          <span className="brand-story-highlight-mark" aria-hidden="true" />
          Formulated through <span className="years-emph">5 years</span> of relentless
          research, innovation, and dedication to intimate wellness.
        </div>
      </div>

      {/* Product collage — INTIVIV™ launch lineup */}
      <ProductLineupCarousel />


      {/* Tagline strip */}
      <div className="brand-tagline" data-testid="home-tagline">
        India's first <span className="gold-italic">Gynaecologist-Led AI</span> Intimate Wellness Ecosystem.<br />
        Medical expertise + AI personalisation.<br />
        Without judgement. Without fear.
      </div>

      {/* Closing manifesto line */}
      <div className="brand-closing" data-testid="home-closing">
        <span className="quote">Because every touch, thought, and truth deserves <em style={{ fontStyle: 'italic' }}>Intimacy.</em></span>
      </div>
    </div>
  );
}

// ── Product Lineup Carousel — swipe through each product one by one ────
const LINEUP = [
  { src: '/products/cleanser.webp',    name: 'Intimate Balance Cleanser',     meta: 'Daily intimate hygiene' },
  { src: '/products/moisturizer.webp', name: 'Intimate Comfort Moisturizer',  meta: 'Lasting hydration' },
  { src: '/products/mist.webp',        name: 'Intimate Freshness Mist',       meta: 'Odour control · freshness' },
  { src: '/products/lubricant.webp',   name: 'Intimate Silk Lubricant',       meta: 'Smooth · pH-balanced' },
  { src: '/products/oil.webp',         name: 'Sensual Intimate Oil',          meta: 'Relaxation · connection' },
];

function ProductLineupCarousel() {
  const navigate = useNavigate();
  const scrollRef = useRef(null);
  const [active, setActive] = useState(0);

  const onScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = el.firstElementChild?.getBoundingClientRect().width || 1;
    const gap = 14;
    const idx = Math.round(el.scrollLeft / (cardWidth + gap));
    setActive(Math.min(Math.max(idx, 0), LINEUP.length - 1));
  };

  const scrollTo = (i) => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = el.firstElementChild?.getBoundingClientRect().width || 1;
    const gap = 14;
    el.scrollTo({ left: (cardWidth + gap) * i, behavior: 'smooth' });
  };

  return (
    <div className="product-collage" data-testid="product-collage">
      <div className="product-collage-eyebrow">The INTIVIV<sup>TM</sup> Lineup</div>

      <div
        className="product-collage-grid"
        ref={scrollRef}
        onScroll={onScroll}
        data-testid="product-collage-scroll"
      >
        {LINEUP.map((p, i) => (
          <motion.div
            key={p.name}
            className="product-collage-card"
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.4, delay: i * 0.05 }}
            data-testid={`product-collage-${i}`}
          >
            <img src={p.src} alt={p.name} loading="lazy" />
            <div className="product-collage-name">{p.name}</div>
            <div className="product-collage-meta">{p.meta}</div>
          </motion.div>
        ))}
      </div>

      {/* Pagination dots */}
      <div className="product-collage-dots" data-testid="product-collage-dots">
        {LINEUP.map((_, i) => (
          <button
            key={i}
            aria-label={`Show product ${i + 1}`}
            className={`product-collage-dot ${i === active ? 'active' : ''}`}
            onClick={() => scrollTo(i)}
            data-testid={`product-collage-dot-${i}`}
            style={{ border: 'none', padding: 0, cursor: 'pointer' }}
          />
        ))}
      </div>

      <div className="product-collage-hint" aria-hidden="true">
        <i className="fa-solid fa-arrow-left" />
        Swipe
        <i className="fa-solid fa-arrow-right" />
      </div>

      <div className="product-collage-launching" data-testid="product-collage-launching">
        <span className="dot" />
        Launching Soon
        <span className="dot" />
      </div>

      <div style={{ textAlign: 'center' }}>
        <button className="lineup-ritual-link" onClick={() => navigate('/ritual')} data-testid="lineup-ritual-link">
          <i className="fa-solid fa-spa" /> Explore The INTIVIV Ritual <i className="fa-solid fa-arrow-right" />
        </button>
      </div>
    </div>
  );
}

